import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { getBankAccount } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investor.service';
import { linkInvestmentBankAccount } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import {
	excludeRejectedBankAccounts,
	isContinueBlockedByBankAccount,
} from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.utils';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { useParams } from 'react-router-dom';
import { resolveStepBankAccount } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestmentBankAccountStep.utils';

interface Props {
	pivPersonId?: string;
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	setIsLoading: (isLoading: boolean) => void;
}

/**
 * Shared by the joiner, leaver and standalone-KYC bank-account steps. Loads the accounts the step
 * edits and, on Continue, links the account shown to the investment before the flow advances.
 *
 * A standalone KYC has no investment to link to — the account is collected against the person and
 * that is the whole job there — so the link is skipped rather than the step being unavailable.
 */
export const useInvestmentBankAccountStep = ({
	pivPersonId,
	setIsContinueDisabled,
	setIsLoading,
}: Props) => {
	// From the route, not the investor request: on a reload the request is still in flight,
	// and a step that does not know its deal yet adopts another deal's bank account.
	const { dealId } = useRequiredParams('dealId');
	const { investmentWorkflowId, kycWorkflowId } = useParams();
	const tInvestor = useTranslationInvestor();

	const bankAccountDataRequest = useQuery({
		queryKey: ['bankAccounts', !!pivPersonId],
		queryFn: (options) => getBankAccount(!!pivPersonId, options.signal),
		select: excludeRejectedBankAccounts,
		enabled: false,
	});

	const stepBankAccount = useMemo(
		() =>
			resolveStepBankAccount(bankAccountDataRequest.data, {
				personId: pivPersonId,
				dealId,
			}),
		[bankAccountDataRequest.data, pivPersonId, dealId]
	);

	useEffect(() => {
		setIsContinueDisabled(
			isContinueBlockedByBankAccount(stepBankAccount ? [stepBankAccount] : [])
		);
	}, [stepBankAccount]);

	const { mutateAsync: linkBankAccount } = useMutation({
		mutationFn: (variables: Parameters<typeof linkInvestmentBankAccount>[0]) =>
			linkInvestmentBankAccount(variables),
	});

	const bankAccountIdToLink = stepBankAccount?.id;

	const link = async (bankAccountId: string): Promise<void> => {
		if (!investmentWorkflowId) {
			return;
		}
		setIsLoading(true);
		try {
			await linkBankAccount({ investmentWorkflowId, bankAccountId });
		} finally {
			setIsLoading(false);
		}
	};

	useWizardStep(async (intent) => {
		if (intent !== 'next') {
			// Leaving the step by Back or the sidebar links too, but never blocks the navigation.
			try {
				if (bankAccountIdToLink) {
					await link(bankAccountIdToLink);
				}
			} catch {
				// navigating away is never blocked by a failed link
			}
			return SAVE_OK;
		}

		if (!bankAccountIdToLink) {
			return SAVE_INVALID;
		}

		try {
			await link(bankAccountIdToLink);
			return SAVE_OK;
		} catch {
			showToastV2({
				title: tInvestor('investment.toast.failed_link_bank_account'),
				state: 'error',
			});
			return SAVE_FAILED;
		}
	});

	return {
		dealId,
		investmentWorkflowId,
		kycWorkflowId,
		bankAccountsData: bankAccountDataRequest.data ?? [],
		bankAccountsLoading: bankAccountDataRequest.isLoading,
		refetchBankAccounts: bankAccountDataRequest.refetch,
	};
};
